/**
 * Notification-rules API (TMMAPP-0021, design §11.6 / TMMAPI-0012) — generated wire types in,
 * normalized domain out. Rules are per envelope per caller; `kind` is never patched (a different
 * kind is a new rule). The caller refetches after a write rather than splicing the list.
 */
import { request } from './client';
import { ApiShapeError } from './errors';
import type {
  MyRuleList,
  MyRuleView,
  NotificationRuleCreateRequest,
  NotificationRulePatchRequest,
  NotificationRuleResponse,
} from './generated';

export type AlertKind = 'BALANCE_BELOW' | 'BALANCE_ABOVE' | 'LARGE_ALLOCATION';

export interface NotificationRule {
  id: number;
  kind: AlertKind;
  thresholdMinor: number;
  currency: string;
  enabled: boolean;
  lastFiredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A rule on `GET /v1/me/notification-rules`, which also names the envelope and virtual account. */
export interface MyNotificationRule extends NotificationRule {
  virtualAccountId: number;
  envelopeId: number;
  envelopeName: string;
}

export interface NotificationRuleCreate {
  kind: AlertKind;
  thresholdMinor: number;
  enabled?: boolean;
}

/** Threshold / enabled only — `kind` is never sent. */
export interface NotificationRulePatch {
  thresholdMinor?: number;
  enabled?: boolean;
}

const KINDS: ReadonlySet<string> = new Set(['BALANCE_BELOW', 'BALANCE_ABOVE', 'LARGE_ALLOCATION']);

/** A rule row; `null` (dropped) for a kind this build doesn't know. */
export function normalizeNotificationRule(w: NotificationRuleResponse): NotificationRule | null {
  if (!KINDS.has(w.kind)) return null;
  return {
    id: w.id,
    kind: w.kind,
    thresholdMinor: w.thresholdMinor,
    currency: w.currency,
    enabled: w.enabled,
    lastFiredAt: w.lastFiredAt ?? null,
    createdAt: w.createdAt,
    updatedAt: w.updatedAt,
  };
}

export function normalizeMyNotificationRule(w: MyRuleView): MyNotificationRule | null {
  const rule = normalizeNotificationRule(w);
  if (!rule) return null;
  return {
    ...rule,
    virtualAccountId: w.virtualAccountId,
    envelopeId: w.envelopeId,
    envelopeName: w.envelopeName,
  };
}

export function normalizeMyNotificationRules(w: MyRuleList): MyNotificationRule[] {
  return w.items.flatMap((row) => {
    const rule = normalizeMyNotificationRule(row);
    return rule ? [rule] : [];
  });
}

export function normalizeNotificationRules(
  value: readonly NotificationRuleResponse[],
): NotificationRule[] {
  return value.flatMap((row) => {
    const rule = normalizeNotificationRule(row);
    return rule ? [rule] : [];
  });
}

/** A created or patched rule; an unknown kind is a contract break here, not a row to drop. */
function required(wire: NotificationRuleResponse): NotificationRule {
  const rule = normalizeNotificationRule(wire);
  if (!rule) throw new ApiShapeError(`notificationRule.kind ${wire.kind} is unknown`);
  return rule;
}

export function toCreateRequest(body: NotificationRuleCreate): NotificationRuleCreateRequest {
  const json: NotificationRuleCreateRequest = {
    kind: body.kind,
    thresholdMinor: body.thresholdMinor,
  };
  if (body.enabled !== undefined) json.enabled = body.enabled;
  return json;
}

/** Only the changed keys; `kind` is never sent, so the stored kind stays. */
export function toPatchRequest(body: NotificationRulePatch): NotificationRulePatchRequest {
  const json: NotificationRulePatchRequest = {};
  if (body.thresholdMinor !== undefined) json.thresholdMinor = body.thresholdMinor;
  if (body.enabled !== undefined) json.enabled = body.enabled;
  return json;
}

const base = (envelopeId: number) => `/v1/envelopes/${envelopeId}/notification-rules`;

export const notificationRulesApi = {
  /** `SUBSCRIPTION_REQUIRED` is rethrown as-is — the caller never lists for a guest. */
  async list(envelopeId: number): Promise<NotificationRule[]> {
    return normalizeNotificationRules(
      await request<NotificationRuleResponse[]>('GET', base(envelopeId)),
    );
  },
  async create(envelopeId: number, body: NotificationRuleCreate): Promise<NotificationRule> {
    return required(
      await request<NotificationRuleResponse>('POST', base(envelopeId), {
        json: toCreateRequest(body),
      }),
    );
  },
  async patch(
    envelopeId: number,
    ruleId: number,
    body: NotificationRulePatch,
  ): Promise<NotificationRule> {
    return required(
      await request<NotificationRuleResponse>('PATCH', `${base(envelopeId)}/${ruleId}`, {
        json: toPatchRequest(body),
      }),
    );
  },
  async remove(envelopeId: number, ruleId: number): Promise<void> {
    await request<void>('DELETE', `${base(envelopeId)}/${ruleId}`);
  },
  /** GET /v1/me/notification-rules — the caller's rules across envelopes. */
  async listMine(): Promise<MyNotificationRule[]> {
    return normalizeMyNotificationRules(
      await request<MyRuleList>('GET', '/v1/me/notification-rules'),
    );
  },
};
